<template>
  <ElTable
    ref="table"
    v-loading="loading"
    class="cohort-report__table"
    :data="computedDataInternal"
    height="100%"
    max-height="100%"
    disable-sort
    border
    :empty-text="$t('reports.cohort.generate_report')"
    :lazy-loading="false"
    :show-header="true"
    :show-summary="false"
    :span-method="objectSpanMethodInternal"
    element-loading-custom-class="data-loader-spinner"
    :row-class-name="tableRowClassInternal"
  >
    <template v-if="computedNamesInternal.length">
      <ElTableColumn
        prop="cohort"
        width="120"
        fixed
        :resizable="false"
        class-name="disable-hover"
      >
        <template v-slot:header>
          <span
            class="row-value date-range"
            v-html="$t('reports.cohort.cohort')"
          />
        </template>
        <template v-slot="scope">
          <span
            class="row-value bold date-range"
            v-html="scope.row.cohort"
          />
        </template>
      </ElTableColumn>
      <ElTableColumn
        prop="metric"
        width="120"
        fixed
        class-name="transparent-hover"
        :resizable="false"
      >
        <template v-slot:header>
          <span
            class="row-value"
            v-html="$t('reports.cohort.metric')"
          />
        </template>
        <template v-slot="scope">
          <span
            class="row-value"
            :class="{ bold: appliedAdditionalMetrics.length > 0 && scope.row.metricKey === 'players' }"
            v-html="scope.row.metric"
          />
        </template>
      </ElTableColumn>
      <ElTableColumn
        v-for="(col, index) in computedNamesInternal"
        :key="index"
        :prop="col"
        :show-overflow-tooltip="true"
        :resizable="false"
        align="right"
        header-align="left"
        :min-width="114"
      >
        <template v-slot:header="scope">
          <span
            :data-column="col"
            class="row-value date-range"
            v-html="renderCohortLabel(scope.column.property)"
          />
        </template>
        <template v-slot="scope">
          <span
            class="row-value"
            :class="{ bold: appliedAdditionalMetrics.length > 0 && scope.row.metricKey === 'players' }"
          >
            {{ scope.row[col] }}
          </span>
        </template>
      </ElTableColumn>
    </template>
  </ElTable>
</template>

<script>
export default {
  name: 'CohortTable',
  props: {
    loading: Boolean,
    computedData: Array,
    computedNames: Array,
    appliedAdditionalMetrics: Array,
    tableData: Object,
    metricsList: Array,
    columnMinWidth: { type: Number, default: 114 },
    rowMinHeight: { type: Number, default: 34 },
    chunkColumnSize: { type: Number, default: 15 },
    chunkRowSize: { type: Number, default: 20 },
    renderCohortLabel: Function,
  },
  data() {
    return {
      chunkColumnIndex: 1,
      chunkRowIndex: 1,
      observer: null,
    };
  },
  computed: {
    chunkComputedNames() {
      return this.chunkArray(this.tableData.cohort_names, this.chunkColumnSize);
    },
    computedNamesInternal() {
      return [-1, 0, 1].flatMap(
        (offset) => this.$_.get(this.chunkComputedNames, [this.chunkColumnIndex + offset], []),
      );
    },
    chunkComputedData() {
      const data = this.getDataComputed(this.tableData.data, this.tableData.cohort_names, this.metricsList);
      return this.chunkArray(data, this.chunkRowSize, this.metricsList.length);
    },
    computedDataInternal() {
      return [-1, 0, 1].flatMap(
        (offset) => this.$_.get(this.chunkComputedData, [this.chunkRowIndex + offset], []),
      );
    },
  },
  methods: {
    chunkArray(arr, approxSize, extraArraySize) {
      const chunkSize = extraArraySize ? Math.ceil(approxSize / extraArraySize) * extraArraySize : approxSize;
      return arr.reduce((result, item, index) => {
        if (index % chunkSize === 0) {
          result.push([item]);
        } else {
          result[result.length - 1].push(item);
        }
        return result;
      }, []);
    },
    updateTable({ chunkColumnIndex, chunkRowIndex, type }) {
      const tableBodyWrapper = this.$refs.table.$el.querySelector('.el-table__body-wrapper .el-scrollbar .el-scrollbar__wrap');
      tableBodyWrapper.style.overflow = 'hidden';
      const prevComputedNamesLen = this.computedNamesInternal.length;
      this.chunkColumnIndex = chunkColumnIndex || this.chunkColumnIndex;
      this.chunkRowIndex = chunkRowIndex || this.chunkRowIndex;

      requestAnimationFrame(() => {
        if (type === 'right') {
          const activeChunk = this.chunkComputedNames[this.chunkColumnIndex];
          const target = activeChunk
            ? this.$refs.table.$el.querySelector(`thead th [data-column="${activeChunk[activeChunk.length - 1]}"]`)?.closest('th')
            : null;
          if (tableBodyWrapper && target) {
            const targetRight = target.offsetLeft + target.offsetWidth;
            tableBodyWrapper.scrollLeft = targetRight - tableBodyWrapper.clientWidth;
          }
        }
        if (type === 'left') {
          const activeChunk = this.chunkComputedNames[this.chunkColumnIndex];
          const target = activeChunk
            ? this.$refs.table.$el.querySelector(`thead th [data-column="${activeChunk[0]}"]`)?.closest('th')
            : null;
          if (tableBodyWrapper && target) {
            const targetLeft = target.offsetLeft;
            tableBodyWrapper.scrollLeft = targetLeft - 240;
          }
        }
        if (type === 'bottom') {
          const activeChunk = this.chunkComputedData[this.chunkRowIndex];
          const target = activeChunk
            ? tableBodyWrapper.querySelector(`tr.row-${activeChunk[activeChunk.length - 1].key}`)
            : null;
          if (tableBodyWrapper && target) {
            const targetBottom = target.offsetTop + target.offsetHeight;
            tableBodyWrapper.scrollTop = targetBottom - tableBodyWrapper.clientHeight;
          }
        }
        if (type === 'top') {
          const activeChunk = this.chunkComputedData[this.chunkRowIndex];
          const target = activeChunk
            ? tableBodyWrapper.querySelector(`tr.row-${activeChunk[0].key}`)
            : null;
          if (tableBodyWrapper && target) {
            tableBodyWrapper.scrollTop = target.offsetTop;
          }
        }
        setTimeout(() => {
          tableBodyWrapper.style.overflow = 'auto';
        }, 100);
      });

      if (prevComputedNamesLen !== this.computedNamesInternal.length) {
        this.$nextTick(() => {
          this.$refs.table.doLayout();
          this.observeEdges();
        });
      }
    },
    setCohortRangeD(type) {
      if (type === 'right' && this.chunkColumnIndex < this.chunkComputedNames.length - 2) {
        this.updateTable({ chunkColumnIndex: this.chunkColumnIndex + 1, type });
      }
      if (type === 'left' && this.chunkColumnIndex > 1) {
        this.updateTable({ chunkColumnIndex: this.chunkColumnIndex - 1, type });
      }
      if (type === 'top' && this.chunkRowIndex > 1) {
        this.updateTable({ chunkRowIndex: this.chunkRowIndex - 1, type });
      }
      if (type === 'bottom' && this.chunkRowIndex < this.chunkComputedData.length - 2) {
        this.updateTable({ chunkRowIndex: this.chunkRowIndex + 1, type });
      }
    },
    setCohortRange(type) {
      this.setCohortRangeThrottled(type);
    },
    observeEdges() {
      if (this.observer) {
        this.observer.disconnect();
      }

      const tableBodyWrapper = this.$refs.table.$el.querySelector('.el-table__body-wrapper .el-scrollbar .el-scrollbar__wrap');
      if (!tableBodyWrapper || !this.computedNamesInternal.length || !this.computedDataInternal.length) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const type = entry.target.dataset.scrollType;
              this.setCohortRange(type);
            }
          });
        },
        { root: this.$refs.table.$el, threshold: 0.1 },
      );
      const lastCol = this.$refs.table.$el.querySelector(`thead th [data-column="${this.computedNamesInternal[this.computedNamesInternal.length - 1]}"]`)?.closest('th');
      const firstCol = this.$refs.table.$el.querySelector(`thead th [data-column="${this.computedNamesInternal[0]}"]`)?.closest('th');
      const lastRow = tableBodyWrapper.querySelector(`tr.row-${this.computedDataInternal[this.computedDataInternal.length - 1].key}`);
      const firstRow = tableBodyWrapper.querySelector(`tr.row-${this.computedDataInternal[0].key}`);

      if (lastCol) {
        lastCol.dataset.scrollType = 'right';
        this.observer.observe(lastCol);
      } else {
        setTimeout(() => {
          this.$nextTick(() => this.observeEdges());
        }, 500);
        return;
      }
      if (firstCol) {
        firstCol.dataset.scrollType = 'left';
        this.observer.observe(firstCol);
      } else {
        setTimeout(() => {
          this.$nextTick(() => this.observeEdges());
        }, 500);
        return;
      }
      if (lastRow) {
        lastRow.dataset.scrollType = 'bottom';
        this.observer.observe(lastRow);
      } else {
        setTimeout(() => {
          this.$nextTick(() => this.observeEdges());
        }, 500);
        return;
      }
      if (firstRow) {
        firstRow.dataset.scrollType = 'top';
        this.observer.observe(firstRow);
      } else {
        setTimeout(() => {
          this.$nextTick(() => this.observeEdges());
        }, 500);
      }
    },
    resetData() {
      this.chunkColumnIndex = 1;
      this.chunkRowIndex = 1;
    },
    tableRowClassInternal({ row, rowIndex }) {
      const isHighlighted = rowIndex % (this.metricsList.length*2) === this.metricsList.length;

      return `row-${row.key} ${isHighlighted ? 'cohort-report__table-row_highlighted' : ''}`;
    },
    objectSpanMethodInternal({ column, row }) {
      if (column.property === 'cohort') {

        if (row.showCohort) {
          return {
            rowspan: this.metricsList.length,
            colspan: 1,
          };
        }

        return {
          rowspan: 0,
          colspan: 0,
        };
      }

      return {
        rowspan: 1,
        colspan: 1,
      };
    },
    getDataComputed(data, cohorts, metricsList) {
      const rows = [];
      cohorts.forEach((cohort) => {
        const cohortData = data[cohort] || {};
        metricsList.forEach((metric, metricIndex) => {
          const row = {
            cohort: this.renderCohortLabel(cohort),
            metric: metric.label,
            metricKey: metric.key,
            showCohort: metricIndex === 0,
            key: `${cohort}-${metric.key}`,
          };
          cohorts.forEach((period) => {
            if (cohortData[period]) {
              const cell = cohortData[period];
              if (metric.key === 'players') {
                row[period] = `${cell.players_count} / ${cell.players_percent_of_cohort}`;
              } else {
                row[period] = cell[metric.key] !== undefined ? cell[metric.key] : '';
              }
            } else {
              row[period] = '';
            }
          });
          rows.push(row);
        });
      });
      return rows;
    },
  },
  watch: {
    tableData: {
      handler() {
        this.observeEdges();
      },
    },
  },
  mounted() {
    this.setCohortRangeThrottled = this.$_.throttle(this.setCohortRangeD, 500);
  },
  activated() {
    if (this.tableData.cohort_names.length) {
      this.observeEdges();
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>

<style lang="scss">
.old-table .cohort-report__table-row_highlighted {
  .disable-hover {
    background: #f3f3f3 !important;
  }
}
</style>
